import React from 'react'
import { getElement } from '../ChatModals/MediaViewerModal/helpers'

import useMediaHandle from '@SonarCX/hooks/useMediaHandle'
import LoadingSpinner from '@SonarCX/components/atoms/LoadingSpinner'
import BaseModal from '../ChatModals/BaseModal2'
import Navigator from './Navigator'

import useStyles from './styles'

interface MediaViewerCarouselProps {
  file: Media
  files: Media[]
  onClose: () => void
}
const MediaViewerCarousel: React.FC<MediaViewerCarouselProps> = ({
  file: media,
  files = [],
  onClose
}) => {
  const [selectedMedia, setSelectedMedia] = React.useState(media)
  const [elem, setElem] = React.useState(null)
  const [closing, setClosing] = React.useState(false)

  const {
    reset,
    startLoad,
    finishLoad,
    download,
    file,
    loading,
    isViewable,
    srcURL
  } = useMediaHandle({ media: selectedMedia, manualEndLoad: true })

  const classes = useStyles()

  const selectedFileIndex = React.useMemo(
    () => files.findIndex(f => f.id === selectedMedia.id),
    [files, selectedMedia]
  )

  const navigate = React.useCallback(
    (index: number) => {
      setSelectedMedia(files[index])
      reset()
      startLoad()
    },
    [files, reset, startLoad]
  )

  const handleKeydown = React.useCallback(
    e => {
      if (files.length === 0 || files.length === 1) return

      if (e.key === 'ArrowLeft' && selectedFileIndex !== 0) {
        navigate(selectedFileIndex - 1)
      } else if (
        e.key === 'ArrowRight' &&
        selectedFileIndex !== files.length - 1
      ) {
        navigate(selectedFileIndex + 1)
      }
    },
    [files, navigate, selectedFileIndex]
  )

  // Closes the modal
  React.useEffect(() => {
    if (closing) {
      setClosing(false)
      onClose()
    }
  }, [closing, onClose])

  // Resets state
  const handleClose = React.useCallback(() => {
    setElem(null)
    setClosing(true)

    reset()
  }, [reset])

  React.useEffect(() => {
    startLoad()
  }, [startLoad])

  // Show a file or download a file that can't be shown
  React.useEffect(() => {
    if (!file || !selectedMedia) {
      return
    }

    if (!isViewable) {
      download()
      return
    }

    getElement({
      media: selectedMedia,
      file,
      classes,
      finishLoad,
      url: srcURL
    }).then(elem => {
      setElem(elem)
    })
  }, [
    file,
    selectedMedia,
    isViewable,
    classes,
    finishLoad,
    srcURL,
    download,
    handleClose
  ])

  React.useEffect(() => {
    document.addEventListener('keydown', handleKeydown)

    return () => document.removeEventListener('keydown', handleKeydown)
  }, [handleKeydown])

  return (
    <>
      <BaseModal
        open
        onClose={handleClose}
        title={selectedMedia?.filename ?? 'Media'}
        PaperProps={{
          style: {
            minWidth: '10vw',
            maxWidth: '80vw',
            minHeight: '20svh',
            maxHeight: '90svh'
          }
        }}
        childrenClass={classes.childrenClass}
      >
        <div>
          {selectedFileIndex + 1} / {files.length}
        </div>

        {!isViewable ? (
          <>
            <div>Preview not available, file has been downloaded</div>

            <Navigator
              files={files}
              navigate={navigate}
              selectedFileIndex={selectedFileIndex}
            />
          </>
        ) : (
          <>
            {loading && <LoadingSpinner className={classes.loading} />}

            {elem}

            {!loading && !!elem && (
              <Navigator
                files={files}
                navigate={navigate}
                selectedFileIndex={selectedFileIndex}
              />
            )}
          </>
        )}
      </BaseModal>
    </>
  )
}

export default MediaViewerCarousel
